// 提示音合成器：用 Web Audio API 现场合成柔和悦耳的提示音，
// 取代外部 mixkit MP3（不稳定、需联网、音色生硬）。
// 优点：音色可控且温和、零延迟、离线可用、可轻松提供多种选择。

let _ctx = null;

// 单例 AudioContext。必须在用户手势内首次 resume（见 unlockAudio），
// 之后无手势上下文的心流提示音也能正常发声。
// iOS Safari 音频被系统中断（来电/切后台）后 state 会变成 "interrupted"，
// 或被系统 "closed"；这两种状态下旧上下文已不可用，需要重建。
export function getAudioCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  // 上下文被关闭后无法复用，丢弃重建。
  if (_ctx && _ctx.state === "closed") _ctx = null;
  if (!_ctx) {
    try {
      _ctx = new AC();
    } catch (e) {
      return null;
    }
  }
  return _ctx;
}

// 在用户手势（如点击「开始」）内调用，解锁音频上下文。
// 返回一个 resolve 后可安全排程的 Promise（上下文已 running）。
export function unlockAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return Promise.resolve(null);
  // suspended / interrupted 都需要 resume 才能推进 currentTime。
  if (ctx.state !== "running" && typeof ctx.resume === "function") {
    return ctx.resume().then(
      () => ctx,
      () => ctx
    );
  }
  return Promise.resolve(ctx);
}

// 每个提示音由若干「声部」叠加而成。每个声部是一个带柔和包络的振荡器：
//   freq    基频(Hz)
//   delay   相对起始的延迟(s)，用于做琶音/错落
//   dur     衰减时长(s)
//   type    波形 sine|triangle|sawtooth
//   gain    峰值音量
//   attack  起振时间(s)，默认 5ms；钟/锣可设长一些做缓入
//   detune  失谐(音分)，叠加做合唱/铃感
//   glideTo 频率滑音目标(Hz)，用于水滴下滑
function playVoice(ctx, dest, v, t0) {
  const start = t0 + (v.delay || 0);
  const dur = v.dur || 1;
  const peak = v.gain != null ? v.gain : 0.3;
  const attack = v.attack != null ? v.attack : 0.005;

  const osc = ctx.createOscillator();
  osc.type = v.type || "sine";
  osc.frequency.setValueAtTime(v.freq, start);
  if (v.glideTo) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(1, v.glideTo),
      start + dur
    );
  }
  if (v.detune) osc.detune.setValueAtTime(v.detune, start);

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(peak, start + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);

  osc.connect(g).connect(dest);
  osc.start(start);
  osc.stop(start + dur + 0.05);
}

// 各提示音的合成配方。键与 data.js 中 SOUNDS 的键一一对应。
const PRESETS = {
  // 默认：柔和玻璃风铃，C6-E6-G6 上行琶音 + 一缕泛音微光
  chime: {
    voices: [
      { freq: 1046.5, delay: 0.0, dur: 1.4, gain: 0.3 },
      { freq: 1318.51, delay: 0.1, dur: 1.5, gain: 0.26 },
      { freq: 1567.98, delay: 0.2, dur: 1.7, gain: 0.24 },
      { freq: 2093.0, delay: 0.2, dur: 0.8, gain: 0.05 },
    ],
  },

  // 水滴：带下滑音的单点 + 余韵
  droplet: {
    voices: [
      { freq: 1200, glideTo: 600, delay: 0, dur: 0.5, gain: 0.34, attack: 0.002 },
      { freq: 600, delay: 0.18, dur: 0.6, gain: 0.12 },
    ],
  },

  // 马林巴：木质三角波双音 + 一点起振泛音
  marimba: {
    voices: [
      { freq: 783.99, delay: 0.0, dur: 0.5, type: "triangle", gain: 0.3 },
      { freq: 3135.96, delay: 0.0, dur: 0.16, gain: 0.04 },
      { freq: 1046.5, delay: 0.14, dur: 0.6, type: "triangle", gain: 0.26 },
      { freq: 4186.0, delay: 0.14, dur: 0.16, gain: 0.04 },
    ],
  },

  // 钟声：以非谐分音模拟温暖铜钟（基频 C5，比 1:2:2.76:5.4）
  bell: {
    voices: [
      { freq: 523.25, dur: 2.6, gain: 0.3, attack: 0.003 },
      { freq: 1046.5, dur: 1.8, gain: 0.15, attack: 0.003 },
      { freq: 1444.17, dur: 1.4, gain: 0.1, attack: 0.003 },
      { freq: 2825.55, dur: 0.9, gain: 0.05, attack: 0.003 },
    ],
  },

  // 水晶（Pro）：E6-G6-B6 高频微失谐合唱，长衰减闪烁
  crystal: {
    voices: [
      { freq: 1318.51, dur: 2.0, gain: 0.2 },
      { freq: 1318.51, detune: 9, dur: 2.0, gain: 0.1 },
      { freq: 1567.98, delay: 0.06, dur: 2.2, gain: 0.18 },
      { freq: 1975.53, delay: 0.12, dur: 2.4, gain: 0.15 },
    ],
  },

  // 锣（Pro）：低频缓入 + 非谐分音，绵长
  gong: {
    voices: [
      { freq: 130.81, dur: 3.0, gain: 0.34, attack: 0.09 },
      { freq: 261.62, dur: 2.4, gain: 0.14, attack: 0.07 },
      { freq: 361.04, dur: 2.0, type: "triangle", gain: 0.08, attack: 0.06 },
      { freq: 196.21, detune: 7, dur: 2.6, gain: 0.1, attack: 0.08 },
    ],
  },

  // 古琴（Pro）：五声音阶拨弦感，三角波快速衰减
  koto: {
    voices: [
      { freq: 587.33, delay: 0.0, dur: 0.7, type: "triangle", gain: 0.22 },
      { freq: 783.99, delay: 0.16, dur: 0.7, type: "triangle", gain: 0.2 },
      { freq: 1046.5, delay: 0.32, dur: 0.95, type: "triangle", gain: 0.18 },
    ],
  },

  // 微提示（随机睁眼提醒用，列表中隐藏）：两声轻盈短音
  ping: {
    voices: [
      { freq: 1318.51, delay: 0.0, dur: 0.22, gain: 0.22 },
      { freq: 1760.0, delay: 0.1, dur: 0.3, gain: 0.2 },
    ],
  },
};

// 播放某个提示音。masterVolume 0~1。
// 先确保上下文已 running 再排程：挂起/中断态下 currentTime 冻结，
// 直接排程会导致声音在恢复时「迟到爆播」或直接丢失。
export function playChime(key, masterVolume = 0.6) {
  if (key === "mute") return;
  const preset = PRESETS[key] || PRESETS.chime;
  Promise.resolve(unlockAudio()).then((ctx) => {
    if (!ctx || ctx.state !== "running") return;

    const master = ctx.createGain();
    master.gain.value = masterVolume;
    master.connect(ctx.destination);

    const t0 = ctx.currentTime + 0.01;
    let maxEnd = 0;
    preset.voices.forEach((v) => {
      playVoice(ctx, master, v, t0);
      const end = (v.delay || 0) + (v.dur || 1) + 0.1;
      if (end > maxEnd) maxEnd = end;
    });
    // 最长声部结束后断开 master，避免长会话累积悬挂节点。
    setTimeout(() => {
      try {
        master.disconnect();
      } catch (e) {
        /* ignore */
      }
    }, (maxEnd + 0.2) * 1000);
  });
}

// 供 UI 判断某键是否可合成
export function hasChime(key) {
  return key === "mute" || !!PRESETS[key];
}

// ===== 背景环境音（专注白噪音）=====
// 自合成柔和「雨/风」质感的褐噪音（经低通滤波），零联网、离线可用，
// 取代此前不稳定的外链 MP3（CDN 403）。返回 stop 能力由 stopAmbient 提供。
let _ambient = null;

export function startAmbient(volume = 0.15) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (_ambient) return; // 已在播放
  const begin = () => {
    if (ctx.state !== "running") return;
    // 2 秒褐噪音缓冲，循环播放
    const frames = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < frames; i++) {
      const white = Math.random() * 2 - 1;
      // 褐噪音积分器：比白噪音更低沉柔和
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 800;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 1.2); // 缓入
    src.connect(lp).connect(gain).connect(ctx.destination);
    src.start();
    _ambient = { src, gain, lp };
  };
  if (ctx.state !== "running" && typeof ctx.resume === "function") {
    ctx.resume().then(begin, () => {});
  } else {
    begin();
  }
}

export function stopAmbient() {
  if (!_ambient) return;
  const { src, gain } = _ambient;
  _ambient = null;
  try {
    const ctx = getAudioCtx();
    if (ctx && gain) {
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4); // 缓出
    }
    setTimeout(() => {
      try {
        src.stop();
        src.disconnect();
      } catch (e) {
        /* ignore */
      }
    }, 500);
  } catch (e) {
    /* ignore */
  }
}

export function isAmbientOn() {
  return !!_ambient;
}
